import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, XCircle, ExternalLink, Sparkles } from "lucide-react";
import { checkEligibility, INDUSTRIES } from "@/data/visa88Days";
import { PostcodePicker } from "./PostcodePicker";

export function PostcodeChecker({ t, lang }) {
  const [postcode, setPostcode] = useState("");
  const [visa, setVisa] = useState("417");
  const [industryKey, setIndustryKey] = useState("plant_animal");
  const [result, setResult] = useState(null);

  const onCheck = (e) => {
    e?.preventDefault();
    if (!postcode.trim()) return;
    const r = checkEligibility({ postcode, visa, industryKey });
    setResult(r);
  };

  const reasonText = (reason) => {
    switch (reason) {
      case "not_regional": return t.checker.reason_not_regional;
      case "not_northern": return t.checker.reason_not_northern;
      case "industry_not_417": return t.checker.reason_industry_not_417;
      case "industry_not_462": return t.checker.reason_industry_not_462;
      case "invalid_postcode": return t.checker.reason_invalid_postcode;
      default: return "";
    }
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="postcode-checker">
      <div className="mb-8">
        <h3 className="font-heading font-bold text-2xl lg:text-3xl text-[#292524] mb-2">
          {t.checker.title}
        </h3>
        <p className="text-[#57534E]">{t.checker.sub}</p>
      </div>

      <form onSubmit={onCheck} className="card space-y-5">
        {/* Postcode */}
        <div>
          <label className="block mb-2 font-semibold text-[#292524] text-sm">
            {t.checker.postcode_label}
          </label>
          <PostcodePicker
            value={postcode}
            onChange={setPostcode}
            placeholder={t.checker.postcode_ph}
          />
        </div>

        {/* Visa */}
        <div>
          <label className="block mb-3 font-semibold text-[#292524] text-sm">
            {t.checker.visa_label}
          </label>
          <div className="grid grid-cols-2 gap-2">
            {["417", "462"].map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setVisa(v)}
                className={`pill-card !justify-center !text-center ${visa === v ? "active" : ""}`}
                data-testid={`checker-visa-${v}`}
              >
                <span className="font-semibold">{v} WHV</span>
              </button>
            ))}
          </div>
        </div>

        {/* Industry */}
        <div>
          <label className="block mb-3 font-semibold text-[#292524] text-sm">
            {t.checker.industry_label}
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {INDUSTRIES.map((ind) => {
              const disabled = (visa === "417" && !ind.valid_417) || (visa === "462" && !ind.valid_462);
              return (
                <button
                  key={ind.key}
                  type="button"
                  onClick={() => !disabled && setIndustryKey(ind.key)}
                  disabled={disabled}
                  className={`pill-card !text-left ${industryKey === ind.key ? "active" : ""} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                  data-testid={`checker-industry-${ind.key}`}
                >
                  <span className="text-xl mr-1">{ind.icon}</span>
                  <span className="text-xs">{ind.label[lang] || ind.label.en}</span>
                </button>
              );
            })}
          </div>
        </div>

        <button type="submit" className="btn-primary w-full" data-testid="checker-submit-btn">
          <Sparkles className="w-4 h-4" />
          {t.checker.check_btn}
        </button>
      </form>

      {/* Result */}
      <AnimatePresence>
        {result && (
          <motion.div
            key={`${postcode}-${visa}-${industryKey}-${result.eligible}`}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className={`mt-6 card !p-6 border-2 ${result.eligible ? "!border-[#3B8268] !bg-[#F0F7F3]" : "!border-[#D9423E] !bg-[#FDEEEC]"}`}
            data-testid="checker-result"
          >
            <div className="flex items-start gap-4">
              <div className={`flex-shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center ${result.eligible ? "bg-[#3B8268]" : "bg-[#D9423E]"}`}>
                {result.eligible
                  ? <CheckCircle2 className="w-7 h-7 text-white" strokeWidth={2.4} />
                  : <XCircle className="w-7 h-7 text-white" strokeWidth={2.4} />
                }
              </div>
              <div className="flex-1 min-w-0">
                <div className={`font-heading font-bold text-lg leading-tight ${result.eligible ? "text-[#2A5C5A]" : "text-[#9B3B1E]"}`}>
                  {result.eligible ? t.checker.result_eligible : t.checker.result_not_eligible}
                </div>
                <div className="text-sm text-[#57534E] mt-1">
                  {result.state && <span className="font-semibold">{result.state}</span>}
                  {result.state && " — "}
                  Postcode {postcode}
                  {result.industry && ` · ${result.industry.icon} ${result.industry.label[lang] || result.industry.label.en}`}
                </div>
                {!result.eligible && (
                  <div className="mt-3 text-sm text-[#57534E] leading-relaxed">
                    {reasonText(result.reason)}
                  </div>
                )}
                {result.eligible && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    <a
                      href={`https://www.seek.com.au/jobs?keywords=${encodeURIComponent(result.industry?.label.en || "")}&where=${encodeURIComponent(postcode)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-outline !py-2 !px-3 text-xs"
                      data-testid="checker-cta-seek"
                    >
                      <ExternalLink className="w-3.5 h-3.5" /> {t.checker.cta_seek}
                    </a>
                    <a
                      href="https://www.workforceaustralia.gov.au/individuals/jobs/search?harvest=true"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-outline !py-2 !px-3 text-xs"
                      data-testid="checker-cta-harvest"
                    >
                      <ExternalLink className="w-3.5 h-3.5" /> {t.checker.cta_harvest}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
